import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
const publicationsData = require(`../data/${process.env.REACT_APP_PERSON_ID}/publications.json`);
const authorsData = require(`../data/${process.env.REACT_APP_PERSON_ID}/authors.json`);
const institutionsData = require(`../data/${process.env.REACT_APP_PERSON_ID}/institutions.json`);

function PublicationsCharts({ filteredPublications }) {
  const networkRef = useRef(null);
  
  useEffect(() => {
    if (networkRef.current) {
      try {
        createEgoNetwork(filteredPublications || publicationsData.publications);
      } catch (error) {
        console.error("Error creating ego network:", error);
        // Display error message in the visualization area
        d3.select(networkRef.current)
          .append("div")
          .style("padding", "20px")
          .style("color", "red")
          .text("Error creating visualization. Please check the console for details.");
      }
    }
  }, [filteredPublications]);
  
  // Safe parse author ID with institution(s)
  const parseAuthorId = (authorWithInst) => {
    try {
      // Handle null or undefined
      if (!authorWithInst) return { authorId: "", institutions: [] };
      
      // Format: "authorId:institution1,institution2"
      const parts = authorWithInst.toString().split(':');
      const authorId = parts[0] || "";
      const institutions = parts.length > 1 && parts[1] ? parts[1].split(',').filter(Boolean) : [];
      return { authorId, institutions };
    } catch (error) {
      console.error("Error parsing author ID:", error, authorWithInst);
      return { authorId: "", institutions: [] };
    }
  };
  
  const createEgoNetwork = (publications) => {
    if (!networkRef.current) return;
    
    // Clear previous visualization
    d3.select(networkRef.current).selectAll("*").remove();
    
    // Create a map of author IDs to author objects
    const authorMap = {};
    authorsData.authors.forEach(author => {
      if (author && author.id) {
        authorMap[author.id] = author;
      }
    });
    
    // Create a map of institution IDs to institution objects
    const institutionMap = {};
    institutionsData.institutions.forEach(institution => {
      if (institution && institution.id) {
        institutionMap[institution.id] = institution;
        if (institution.name) {
          institutionMap[institution.name] = institution; // Also map by name for backward compatibility
        }
      }
    });
    
    // Extract nodes (authors) and links (co-authorship)
    const nodes = new Map();
    const links = [];
    
    // Add central author (ego) - assuming it's "porduna"
    const egoId = "porduna";
    nodes.set(egoId, {
      id: egoId,
      name: authorMap[egoId]?.name || egoId,
      publications: 0,
      type: "ego",
      institution: null
    });
    
    // Track all authors in publications
    const allAuthors = new Set();
    
    // Process publications to build the network
    if (Array.isArray(publications)) {
      publications.forEach(pub => {
        // Skip invalid publications
        if (!pub || !pub.authors || !Array.isArray(pub.authors)) return;
        
        const authorIds = [];
        
        // Process each author
        pub.authors.forEach(authorWithInst => {
          try {
            const { authorId, institutions } = parseAuthorId(authorWithInst);
            if (authorId) {
              authorIds.push({ authorId, institutions });
              allAuthors.add(authorId);
            }
          } catch (error) {
            console.error("Error processing author:", error, authorWithInst);
          }
        });
        
        // Add all authors as nodes and create links between them
        authorIds.forEach(({ authorId, institutions }) => {
          if (!authorId) return;
          
          if (!nodes.has(authorId)) {
            // Use the most recent institution for this author
            const latestInstitution = institutions.length > 0 ? institutions[institutions.length - 1] : null;
            
            nodes.set(authorId, {
              id: authorId,
              name: authorMap[authorId]?.name || authorId,
              publications: 0,
              type: authorId === egoId ? "ego" : "coauthor",
              institution: latestInstitution
            });
          }
          
          // Increment publication count for this author
          const author = nodes.get(authorId);
          if (author) {
            author.publications += 1;
          }
        });
        
        // Create links between all authors in this publication
        for (let i = 0; i < authorIds.length; i++) {
          const author1 = authorIds[i];
          if (!author1 || !author1.authorId) continue;
          
          for (let j = i + 1; j < authorIds.length; j++) {
            const author2 = authorIds[j];
            if (!author2 || !author2.authorId) continue;
            
            links.push({
              source: author1.authorId,
              target: author2.authorId,
              value: 1
            });
          }
        }
      });
    }
    
    // If ego author is not in any publication, remove it
    if (!allAuthors.has(egoId)) {
      nodes.delete(egoId);
    }
    
    // Combine duplicate links and sum their values
    const linkMap = new Map();
    links.forEach(link => {
      try {
        if (!link.source || !link.target) return;
        
        const key = link.source < link.target 
          ? `${link.source}-${link.target}` 
          : `${link.target}-${link.source}`;
        
        if (!linkMap.has(key)) {
          linkMap.set(key, { ...link });
        } else {
          linkMap.get(key).value += link.value;
        }
      } catch (error) {
        console.error("Error processing link:", error, link);
      }
    });
    
    // Convert nodes and links to arrays for D3
    const nodeArray = Array.from(nodes.values());
    const linkArray = Array.from(linkMap.values());
    
    // If no nodes, show a message
    if (nodeArray.length === 0) {
      d3.select(networkRef.current)
        .append("div")
        .style("padding", "20px")
        .style("text-align", "center")
        .text("No co-authorship data available for the selected filters.");
      return;
    }
    
    // Set up the SVG container with dynamic sizing
    const width = networkRef.current.clientWidth;
    // Calculate height based on number of nodes (more nodes = more space needed)
    const height = Math.max(500, Math.min(3000, nodeArray.length * 50));
    
    // Store the calculated height for the container
    networkRef.current.dataset.calculatedHeight = height;
    
    const svg = d3.select(networkRef.current)
      .append("svg")
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", [-width / 2, -height / 2, width, height])
      .attr("style", "width: 100%; height: " + height + "px;");
    
    // Create a force simulation with more space between nodes
    const simulation = d3.forceSimulation(nodeArray)
      .force("link", d3.forceLink(linkArray).id(d => d.id).distance(200)) // Increased from 150 to 200
      .force("charge", d3.forceManyBody().strength(-600)) // Increased from -400 to -600
      .force("center", d3.forceCenter())
      .force("collide", d3.forceCollide().radius(d => Math.sqrt(d.publications) * 4 + 15)); // Increased for better spacing
    
    // Create links
    const link = svg.append("g")
      .selectAll("line")
      .data(linkArray)
      .join("line")
      .attr("stroke", "#999")
      .attr("stroke-opacity", 0.6)
      .attr("stroke-width", d => Math.sqrt(d.value) * 1.5);
    
    // Create nodes
    const node = svg.append("g")
      .selectAll("g")
      .data(nodeArray)
      .join("g")
      .call(drag(simulation))
      .on("click", function(event, d) {
        // Show tooltip with full name on click
        const tooltip = d3.select(".tooltip");
        tooltip.transition()
          .duration(200)
          .style("opacity", 0.9);
        
        let tooltipContent = `<strong>${d.name || 'Unknown'}</strong><br/>`;
        tooltipContent += `Publications: ${d.publications || 0}`;
        
        tooltip.html(tooltipContent)
          .style("left", (event.pageX + 10) + "px")
          .style("top", (event.pageY - 28) + "px");
      })
      .on("mouseout", function() {
        // Hide tooltip
        d3.select(".tooltip").transition()
          .duration(500)
          .style("opacity", 0);
      });
    
    // Define 100 distinct colors for institutions
    const generateDistinctColors = (count) => {
      const colors = [];
      // Generate colors with good spacing in HSL color space
      for (let i = 0; i < count; i++) {
        // Use golden ratio to get well-distributed hues
        const hue = i * 137.508; // golden angle in degrees
        const h = hue % 360;
        const s = 65 + (i % 3) * 10; // Vary saturation between 65-85%
        const l = 45 + (i % 5) * 5;  // Vary lightness between 45-65%
        colors.push(`hsl(${h}, ${s}%, ${l}%)`);
      }
      return colors;
    };
    
    // Generate 100 distinct colors
    const distinctColors = generateDistinctColors(100);
    const institutionColors = {};
    
    // Collect all institutions used in the network
    const usedInstitutions = new Set();
    nodeArray.forEach(node => {
      if (node.institution) {
        usedInstitutions.add(node.institution);
      }
    });
    
    // Assign colors to used institutions
    Array.from(usedInstitutions).forEach((instId, i) => {
      institutionColors[instId] = distinctColors[i % distinctColors.length];
    });
    
    // Add circles to nodes with more moderate sizing
    node.append("circle")
      .attr("r", d => Math.sqrt(d.publications) * 3.5 + 6) // Slightly increased for better visibility
      .attr("fill", d => {
        if (d.type === "ego") return "#ff7f0e";
        return d.institution && institutionColors[d.institution] 
          ? institutionColors[d.institution] 
          : "#1f77b4";
      })
      .attr("stroke", "#fff")
      .attr("stroke-width", 1.5); // Add white border for better contrast
    
    // Add text labels to nodes
    node.append("text")
      .attr("x", 0)
      .attr("y", d => -Math.sqrt(d.publications) * 5 - 7)
      .attr("text-anchor", "middle")
      .attr("font-size", "10px")
      .text(d => d.name || d.id || "Unknown");
    
    // Create tooltip div if it doesn't exist
    if (d3.select("body").select(".tooltip").empty()) {
      d3.select("body").append("div")
        .attr("class", "tooltip")
        .style("opacity", 0)
        .style("position", "absolute")
        .style("text-align", "center")
        .style("padding", "8px")
        .style("font-size", "12px")
        .style("background", "white")
        .style("border-radius", "4px")
        .style("pointer-events", "none")
        .style("box-shadow", "0 0 5px rgba(0,0,0,0.3)");
    }
    
    // Update positions on each tick of the simulation
    simulation.on("tick", () => {
      link
        .attr("x1", d => d.source.x || 0)
        .attr("y1", d => d.source.y || 0)
        .attr("x2", d => d.target.x || 0)
        .attr("y2", d => d.target.y || 0);
      
      node.attr("transform", d => `translate(${d.x || 0},${d.y || 0})`);
    });
    
    // Drag functionality
    function drag(simulation) {
      function dragstarted(event) {
        if (!event.active) simulation.alphaTarget(0.3).restart();
        event.subject.fx = event.subject.x;
        event.subject.fy = event.subject.y;
      }
      
      function dragged(event) {
        event.subject.fx = event.x;
        event.subject.fy = event.y;
      }
      
      function dragended(event) {
        if (!event.active) simulation.alphaTarget(0);
        event.subject.fx = null;
        event.subject.fy = null;
      }
      
      return d3.drag()
        .on("start", dragstarted)
        .on("drag", dragged)
        .on("end", dragended);
    }
    
    // Add legend if there are institutions
    if (usedInstitutions.size > 0) {
      const legend = svg.append("g")
        .attr("transform", `translate(${-width/2 + 20}, ${-height/2 + 20})`);
      
      // Add legend title
      legend.append("text")
        .attr("x", 0)
        .attr("y", 0)
        .attr("font-weight", "bold")
        .text("Institution Colors");
      
      // Add legend items for institutions
      Array.from(usedInstitutions).forEach((instId, i) => {
        const instName = institutionMap[instId]?.name || instId;
        const color = institutionColors[instId];
        
        legend.append("circle")
          .attr("cx", 10)
          .attr("cy", 20 + i * 20)
          .attr("r", 6)
          .attr("fill", color);
        
        legend.append("text")
          .attr("x", 25)
          .attr("y", 24 + i * 20)
          .attr("font-size", "12px")
          .text(instName);
      });
      
      // Add legend for authors without institution
      if (nodeArray.some(node => !node.institution && node.type !== "ego")) {
        const i = usedInstitutions.size;
        legend.append("circle")
          .attr("cx", 10)
          .attr("cy", 20 + i * 20)
          .attr("r", 6)
          .attr("fill", "#1f77b4");
        
        legend.append("text")
          .attr("x", 25)
          .attr("y", 24 + i * 20)
          .attr("font-size", "12px")
          .text("No Institution");
      }
      
      // Add ego author to legend if present
      if (nodeArray.some(node => node.type === "ego")) {
        const i = usedInstitutions.size + (nodeArray.some(node => !node.institution && node.type !== "ego") ? 1 : 0);
        legend.append("circle")
          .attr("cx", 10)
          .attr("cy", 20 + i * 20)
          .attr("r", 6)
          .attr("fill", "#ff7f0e");
        
        const egoNode = nodeArray.find(node => node.type === "ego");
        legend.append("text")
          .attr("x", 25)
          .attr("y", 24 + i * 20)
          .attr("font-size", "12px")
          .text(egoNode?.name || "You");
      }
    }
  };
  
  // Effect to update container height when D3 calculates it
  useEffect(() => {
    const updateContainerHeight = () => {
      if (networkRef.current && networkRef.current.dataset.calculatedHeight) {
        // Use the height calculated by D3
        networkRef.current.style.height = `${networkRef.current.dataset.calculatedHeight}px`;
      }
    };
    
    // Set up a mutation observer to detect when D3 adds the SVG
    const observer = new MutationObserver(updateContainerHeight);
    
    if (networkRef.current) {
      observer.observe(networkRef.current, { 
        childList: true,
        attributes: true,
        attributeFilter: ['data-calculated-height']
      });
    }
    
    return () => observer.disconnect();
  }, []);

  return (
    <div 
      ref={networkRef} 
      style={{ 
        width: '100%', 
        border: '1px solid #eee', 
        borderRadius: '8px',
        maxHeight: '80vh', // Limit to 80% of viewport height
        height: 'auto',
        overflow: 'auto' // Enable scrolling when content exceeds the container
      }}
    ></div>
  );
}

export default PublicationsCharts;
